export class IndexedDbService {
    private readonly dbName: string;
    private readonly version: number;
    private readonly stores: Array<{ name: string, options: IDBObjectStoreParameters }>;
    private db: IDBDatabase | null;

    constructor(dbName: string, version: number, stores: Array<{ name: string, options: IDBObjectStoreParameters }>) {
        this.dbName = dbName;
        this.version = version;
        this.stores = stores;
        this.db = null;
    }

    public async open(): Promise<void> {
        if (!window || !window.indexedDB) {
            return;
        }

        // Fresh client: one open at the requested version; onupgradeneeded creates stores.
        if (!(await this._dbExists())) {
            this.db = await this._openWithVersion(this.version);
            return;
        }

        const existing = await this._openExisting();
        const isVersionTooHigh = existing.version > this.version;
        const hasMissingStores = this.stores.some(
            (store) => !existing.objectStoreNames.contains(store.name)
        );

        // Downgrade: backup → delete → recreate at requested version → restore.
        if (isVersionTooHigh) {
            const backup = await this._readAllStores(existing);
            existing.close();
            await this._deleteDb();
            this.db = await this._openWithVersion(this.version);
            await this._writeAllStores(this.db, backup);
            return;
        }

        // Missing stores: bump version so onupgradeneeded runs (no delete/recreate).
        if (hasMissingStores) {
            existing.close();
            const targetVersion = Math.max(existing.version, this.version) + 1;
            this.db = await this._openWithVersion(targetVersion);
            return;
        }

        existing.close();
        this.db = await this._openWithVersion(this.version);
    }

    private async _dbExists(): Promise<boolean> {
        if (window.indexedDB.databases) {
            const databases = await window.indexedDB.databases();
            return databases.some((db) => db.name === this.dbName);
        }

        // Fallback for browsers without indexedDB.databases(): open without a
        // version. A DB that was never created comes back as v1 with zero stores
        // (an empty shell) — treat that as "does not exist".
        try {
            const db = await this._openExisting();
            const isEmptyShell = db.version === 1 && db.objectStoreNames.length === 0;
            db.close();
            return !isEmptyShell;
        } catch {
            return false;
        }
    }

    private _openExisting(): Promise<IDBDatabase> {
        return new Promise<IDBDatabase>((resolve, reject) => {
            const request = window.indexedDB.open(this.dbName);
            request.onerror = (event: any) => reject(event.target.error);
            request.onsuccess = (event: any) => resolve(event.target.result);
        });
    }

    private _readAllStores(db: IDBDatabase): Promise<Record<string, any[]>> {
        const storeNames = this.stores
            .map((s) => s.name)
            .filter((name) => db.objectStoreNames.contains(name));

        if (storeNames.length === 0) {
            return Promise.resolve({});
        }

        return new Promise<Record<string, any[]>>((resolve, reject) => {
            const transaction = db.transaction(storeNames, "readonly");
            const result: Record<string, any[]> = {};
            transaction.onerror = (event: any) => reject(event.target.error);
            transaction.oncomplete = () => resolve(result);

            for (const name of storeNames) {
                const request = transaction.objectStore(name).getAll();
                request.onsuccess = (event: any) => {
                    result[name] = event.target.result || [];
                };
            }
        });
    }

    private _writeAllStores(db: IDBDatabase, data: Record<string, any[]>): Promise<void> {
        const storeNames = Object.keys(data).filter(
            (name) => db.objectStoreNames.contains(name) && data[name].length > 0
        );

        if (storeNames.length === 0) {
            return Promise.resolve();
        }

        return new Promise<void>((resolve, reject) => {
            const transaction = db.transaction(storeNames, "readwrite");
            transaction.onerror = (event: any) => reject(event.target.error);
            transaction.oncomplete = () => resolve();

            for (const name of storeNames) {
                const store = transaction.objectStore(name);
                for (const item of data[name]) {
                    store.put(item);
                }
            }
        });
    }

    private _deleteDb(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const request = window.indexedDB.deleteDatabase(this.dbName);
            request.onerror = (event: any) => reject(event.target.error);
            request.onblocked = () => reject(new Error(`Deleting ${this.dbName} is blocked by another connection`));
            request.onsuccess = () => resolve();
        });
    }

    private _openWithVersion(version: number): Promise<IDBDatabase> {
        return new Promise<IDBDatabase>((resolve, reject) => {
            const request = window.indexedDB.open(this.dbName, version);
            request.onerror = (event: any) => reject(event.target.error);
            request.onblocked = () => reject(new Error(`Opening ${this.dbName} (v${version}) is blocked by another connection`));
            request.onupgradeneeded = (event: any) => this._createStores(event.target.result);
            request.onsuccess = (event: any) => resolve(event.target.result);
        });
    }

    public async getAll<T = any>(storeName: string): Promise<T | undefined> {
        return new Promise<T | undefined>((resolve, reject) => {
            if (this.db) {
                const transaction = this.db!.transaction(storeName, "readonly");
                const store = transaction.objectStore(storeName);
                const request = store.getAll();
                // @ts-ignore
                request.onsuccess = (event) => resolve(event.target.result);
                // @ts-ignore
                request.onerror = (event) => reject(event.target.error);
            }
        });
    }

    public async get<T = any>(storeName: string, key: IDBValidKey): Promise<T | undefined> {
        return new Promise<T | undefined>((resolve, reject) => {
            if (this.db) {
                const transaction = this.db!.transaction(storeName, "readonly");
                const store = transaction.objectStore(storeName);
                const request = store.get(key);
                // @ts-ignore
                request.onsuccess = (event) => resolve(event.target.result);
                // @ts-ignore
                request.onerror = (event) => reject(event.target.error);
            }
        });
    }

    public async set(storeName: string, data: any): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (this.db) {
                const transaction = this.db!.transaction(storeName, "readwrite");
                const store = transaction.objectStore(storeName);
                const request = store.put(data);
                request.onsuccess = (event) => resolve();
                // @ts-ignore
                request.onerror = (event) => reject(event.target.error);
            }
        });
    }

    public async delete(storeName: string, key: IDBValidKey): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (this.db) {
                const transaction = this.db!.transaction(storeName, "readwrite");
                const store = transaction.objectStore(storeName);
                const request = store.delete(key);
                request.onsuccess = (event) => resolve();
                // @ts-ignore
                request.onerror = (event) => reject(event.target.error);
            }
        });
    }

    public isDbConnected(): boolean {
        return this.db !== null;
    }

    public async connectWithIndexedDb() {
        try {
            if (!this.isDbConnected()) {
                await this.open();
            }
        } catch (error) {
            console.error(error);
        }
    }

    public close(): void {
        if (this.db) {
            this.db.close();
            this.db = null;
        }
    }

  // public clearIndexDB() {
  //     // open a read/write db transaction, ready for clearing the data
  //     const transaction = this.db!.transaction(["assortmentItemObject"], "readwrite");
  //     if (transaction) {
  //         // create an object store on the transaction
  //         const objectStore = transaction.objectStore("assortmentItemObject");
  //         if (objectStore) {
  //             // Make a request to clear all the data out of the object store
  //             objectStore.clear();
  //         }
  //     }
  // };

  public async clearIndexDB(storeName: string) {
    return new Promise<void>((resolve, reject) => {
      if (this.db) {
        const transaction = this.db!.transaction(storeName, "readwrite");
        if (!transaction) {
          reject(new Error(`Failed to open transaction for store ${storeName} in ${this.dbName}`));
        }
        const objectStore = transaction.objectStore(storeName);
        if (!objectStore) {
          reject(new Error(`Failed to open object store ${storeName} in ${this.dbName}`));
        }
        const request = objectStore.clear();

        request.onsuccess = () => resolve();

        request.onerror = (event: any) => reject(event.target.error);
      } else {
        reject(new Error(`Database not found or not opened`));
      }
    });
  };

    private _createStores(db: IDBDatabase): void {
        for (const store of this.stores) {
            if (!db.objectStoreNames.contains(store.name)) {
                db.createObjectStore(store.name, store.options);
            }
        }
    }
}
